/**
 * @fileoverview ค่าของปุ่ม "ลงประกาศฟรี" ที่ใช้ทั้งเว็บฝั่งลูกค้า
 * @module services/home_post
 *
 * ที่มาของข้อมูล : GET home/post (เปิดสาธารณะ) — ตั้งค่าที่ "จัดการหน้าแรก › ลงประกาศฟรี"
 *   src      รูปในการ์ดลงประกาศฟรี (ไม่ตั้ง = ใช้ภาพจำลองเดิม)
 *   line_id  LINE ID ที่ปุ่มจะพาไปเพิ่มเพื่อน (ไม่ตั้ง = ปุ่มยังแจ้ง "กำลังพัฒนา")
 *
 * ทำไมต้องมีตัวกลางตัวนี้ :
 *   ปุ่มลงประกาศฟรีอยู่บนเมนูและฟุตเตอร์ของทุกหน้าสาธารณะ (หน้าแรก / รายการทรัพย์ /
 *   รายละเอียด / บทความ / รายการโปรด) ถ้าให้แต่ละหน้ายิงเองจะยิงซ้ำและได้ค่าไม่ตรงกัน
 *   จึงโหลดครั้งเดียวต่อการเปิดเว็บแล้วให้ทุกจุดใช้ค่าเดียวกัน — แบบเดียวกับ company.service
 *
 * เก็บ cache ลง localStorage เพื่อให้รูปในการ์ดขึ้นตั้งแต่เฟรมแรกของรอบถัดไป
 * ไม่ต้องเห็นภาพจำลองแวบหนึ่งก่อนแล้วค่อยสลับเป็นรูปจริง
 */

const STORAGE_KEY = "mn-home-post";

/** ค่าตั้งต้น — ทั้งสองช่องว่าง = ยังไม่ได้ตั้งค่าจากหลังบ้าน */
export const EMPTY_POST = {
  src: "",
  line_id: "",
};

let post = readCache();
let request = null;
const listeners = new Set();

function readCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return cached ? { ...EMPTY_POST, ...cached } : { ...EMPTY_POST };
  } catch (e) {
    return { ...EMPTY_POST };
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {}
}

/**
 * ค่าที่ถืออยู่ตอนนี้ (อ่านได้ทันที ไม่ต้องรอ API)
 * @returns {{src: string, line_id: string}}
 */
export const getPost = () => post;

/**
 * รับแจ้งเมื่อค่าเปลี่ยน (โหลดเสร็จ / เพิ่งบันทึกที่หน้าจัดการหน้าแรก)
 * @param {Function} listener
 * @returns {Function} ฟังก์ชันยกเลิกการรับแจ้ง
 */
export const subscribePost = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * ตั้งค่าชุดใหม่แล้วแจ้งทุกจุดที่ใช้อยู่
 * @param {Object} data
 */
export const setPost = (data) => {
  const source = data || {};
  post = Object.keys(EMPTY_POST).reduce((acc, key) => ({ ...acc, [key]: source[key] ?? EMPTY_POST[key] }), {});
  writeCache(post);
  listeners.forEach((listener) => listener(post));
};

/**
 * โหลดค่าจาก API (ครั้งเดียวต่อการเปิดเว็บ)
 *
 * ยิง fetch ตรงแทน GET ของ services/api เพราะอันนั้นเด้ง popup เตือนเมื่อ API ล่ม —
 * งานนี้เป็นงานเบื้องหลังของทุกหน้า ล้มเหลวแล้วควรเงียบแล้วใช้ค่าที่ cache ไว้ต่อ
 *
 * @param {boolean} force - true = ดึงใหม่แม้เคยดึงไปแล้ว
 * @returns {Promise<{src: string, line_id: string}>}
 */
export const loadPost = (force = false) => {
  if (request && !force) return request;

  request = fetch(`${process.env.REACT_APP_API_URL}home/post`, {
    method: "GET",
    headers: { accept: "application/json" },
  })
    .then((response) => response.json())
    .then((result) => {
      if (result?.status === true && result.data) setPost(result.data);
      return post;
    })
    .catch(() => post);

  return request;
};

const homePostService = {
  EMPTY_POST,
  getPost,
  subscribePost,
  setPost,
  loadPost,
};

export default homePostService;
